/**
 * usePresenceHeartbeat — pings POST /presence/heartbeat every ~45s while the
 * app is foregrounded and the user is signed in, so the admin "Live" tab can
 * show who's on the app and on what device. Best-effort: a failed ping never
 * surfaces to the user. Stops on background / sign-out.
 *
 * Call once, high in the tree (RootNavigator).
 */
import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { apiPost } from './apiClient';
import { getSessionIds } from '../store/actions/_sessionIds';
import { getPlatformDevice, getAppVersion } from './devicePlatform';

const HEARTBEAT_MS = 45_000;

export function usePresenceHeartbeat(): void {
  const authorized = useSelector((s: RootState) => s.session.authorized);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const ping = async () => {
      try {
        const { accountId, cognitoId } = getSessionIds();
        if (!accountId || !cognitoId) return;
        await apiPost(`/presence/heartbeat/${accountId}/${cognitoId}`, {
          platform: getPlatformDevice(),
          app_version: getAppVersion(),
        });
      } catch {
        /* best-effort telemetry — ignore */
      }
    };

    const start = () => {
      if (timer.current) return;
      ping();
      timer.current = setInterval(ping, HEARTBEAT_MS);
    };
    const stop = () => {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
    };

    if (authorized && AppState.currentState === 'active') start();
    else stop();

    const sub = AppState.addEventListener('change', (state) => {
      if (authorized && state === 'active') start();
      else stop();
    });

    return () => { sub.remove(); stop(); };
  }, [authorized]);
}
